/* eslint-disable react-hooks/exhaustive-deps */
import {
  Grid,
  Box,
  Container,
  TextField,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import * as yup from "yup";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, type RootState } from "@store/store";
import { ProgressBar } from "@components/ProgressBar";
import { setCompanyData, setLoanStatus } from "@store/slices/registerSlice";
import { usePatchCuentaMutation } from "@api/account";
import { usePostValidateNextStageMutation } from "@api/applications";
import { transformTitleCase } from "@helpers/funciones";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import useStatusConfig from "@hooks/useStatusConfig";
import useTofuSteps from "@hooks/useTofuSteps";
import CustomContryCode from "@components/CustomContryCode/CustomContryCode";

const SCREEN_STATUS = "CSDEM";

const DatosEmpresaPfae = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const { nextStatus } = useStatusConfig(SCREEN_STATUS);
  const { numberOfSteps, currentStep } = useTofuSteps(SCREEN_STATUS);

  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [triggerPatchCuenta] = usePatchCuentaMutation();

  const companyData = useSelector((state: RootState) => state.register.company);
  const application = useSelector((state: RootState) => state.register.application);
  const personalData = useSelector((state: RootState) => state.register.personal_data);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const formik = useFormik({
    initialValues: {
      name: companyData?.pfae_name || "",
      lastName1: companyData?.pfae_last_name || "",
      lastName2: companyData?.pfae_last_name_2 || "",
      isPFAE: companyData?.is_pfae || false,
      country_code: companyData?.country_code_legal_representative || "+52",
      phone: companyData?.phone_legal_representative || "",
      email: companyData?.email_legal_representative || "",
    },
    validationSchema: yup.object().shape({
      name: yup.string().trim().required("El nombre es obligatorio"),
      lastName1: yup.string().trim().required("El apellido paterno es obligatorio"),
      lastName2: yup.string().trim().nullable(),
      country_code: yup.string().trim().required("Campo requerido"),
      phone: yup.string().trim().required("Campo requerido"),
      email: yup
        .string()
        .email("Asegúrate de incluir el @ y los puntos necesarios (.com .mx)")
        .required("Campo requerido"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatus) {
          throw new Error("No se encontró el siguiente status");
        }
        setSubmitting(true);

        const legal_name = `${values.name} ${values.lastName1} ${values.lastName2 || ""}`.trim();
        const name = values.name.trim();
        const lastName1 = values.lastName1.trim();
        const lastName2 = values.lastName2?.trim();

        await triggerPatchCuenta({
          accountId: personalData.id,
          body: {
            company_legal_name: legal_name,
            is_pfae: values.isPFAE || false,
            pfae_name: name,
            pfae_last_name: lastName1,
            pfae_last_name_2: lastName2,
            country_code_legal_representative: values.country_code,
            phone_legal_representative: values.phone,
            email_legal_representative: values.email,
          },
        }).unwrap();

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatus.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(
          setCompanyData({
            id: personalData.id,
            legal_name: legal_name,
            name: companyData.name,
            rfc: companyData.rfc,
            role: "",
            ciec_pass: "",
            authorize_sat: false,
            is_pfae: values.isPFAE || false,
            pfae_name: name,
            pfae_last_name: lastName1,
            pfae_last_name_2: lastName2,
            is_correct_ciec: false,
            country_code_legal_representative: values.country_code,
            phone_legal_representative: values.phone,
            email_legal_representative: values.email,
          }),
        );

        dispatch(setLoanStatus(nextStatus.code));

        navigate(nextStatus.url);
      } catch (error) {
        // const api_error = error as ApiError;
        // if (api_error?.data?.errors) {
        //   formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
        //   return;
        // }

        Sentry.captureException(error, {
          extra: {
            component: "DatosEmpresaPfae",
            values: formik.values,
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      formik.setValues(
        {
          ...formik.values,
          name: personalData.name || "",
          lastName1: personalData.first_lastname || "",
          lastName2: personalData.second_lastname || "",
          country_code: accountUser.code_phone || "",
          phone: accountUser.phone || "",
          isPFAE: true,
          email: accountUser.email || "",
        },
        true,
      );
    } else {
      formik.setValues(
        {
          ...formik.values,
          name: "",
          lastName1: "",
          lastName2: "",
          country_code: "+52",
          phone: "",
          isPFAE: false,
          email: "",
        },
        true,
      );
    }
  };

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={2}>
        <InactivityHandler />
        <Box
          sx={{
            mt: 2,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            {interfaceConfig.textos_tofu?.pfae?.datos_empresa_pfae?.titulo ||
              "Ingresa los datos de la persona física con actividad empresarial"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <ProgressBar totalSteps={numberOfSteps} currentStep={currentStep} />
          </Container>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3}>
            <Typography
              variant={isMobileOrTablet ? "body2" : "body1"}
              textAlign={"center"}
              color={theme?.palette?.primary?.dark}
            >
              {interfaceConfig.textos_tofu?.pfae?.datos_empresa_pfae?.sub_titulo || "Ingresa los datos de la PFAE"}
            </Typography>
          </Box>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 3 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Grid item sx={{ textAlign: "left", mb: 1, mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox id="one" name="isPFAE" onChange={handleCheckboxChange} checked={formik.values.isPFAE} />
                  }
                  label="Soy la Persona Física con act. empresarial"
                  disabled={formik.isSubmitting || companyData.is_correct_ciec}
                  sx={{ color: theme?.palette?.primary?.dark }}
                />
              </Grid>

              <TextField
                id="name"
                name="name"
                label="Nombre"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue("name", transformTitleCase(e.target.value))}
                value={formik.values.name}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name ? formik.errors.name : ""}
                inputProps={{ maxLength: 40 }}
                disabled={formik.isSubmitting || formik.values.isPFAE}
              />

              <TextField
                id="lastName1"
                name="lastName1"
                label="Apellido Paterno"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue("lastName1", transformTitleCase(e.target.value))}
                value={formik.values.lastName1}
                error={formik.touched.lastName1 && Boolean(formik.errors.lastName1)}
                helperText={formik.touched.lastName1 ? formik.errors.lastName1 : ""}
                inputProps={{ maxLength: 40 }}
                disabled={formik.isSubmitting || formik.values.isPFAE}
              />

              <TextField
                id="lastName2"
                name="lastName2"
                label="Apellido Materno"
                margin="normal"
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => formik.setFieldValue("lastName2", transformTitleCase(e.target.value))}
                value={formik.values.lastName2}
                error={formik.touched.lastName2 && Boolean(formik.errors.lastName2)}
                helperText={formik.touched.lastName2 ? formik.errors.lastName2 : ""}
                inputProps={{ maxLength: 40 }}
                disabled={formik.isSubmitting || formik.values.isPFAE}
              />

              <TextField
                id="email"
                name="email"
                label="Correo electrónico"
                margin="normal"
                required
                fullWidth
                autoComplete="off"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const value_to_set = (e.target.value || "").toLowerCase();
                  formik.setFieldValue("email", value_to_set.trim());
                }}
                value={formik.values.email}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email ? formik.errors.email : ""}
                inputProps={{ maxLength: 100 }}
                disabled={formik.isSubmitting || formik.values.isPFAE}
              />

              <Grid container spacing={1} mt={2}>
                <Grid item xs={5} sm={5} md={5}>
                  <CustomContryCode
                    label="Código"
                    name="country_code"
                    value={formik.values.country_code}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.country_code && Boolean(formik.errors.country_code)}
                    disabled={formik.isSubmitting || formik.values.isPFAE}
                    required={true}
                  />
                </Grid>

                <Grid item xs={7} sm={7} md={7}>
                  <TextField
                    type="text"
                    name="phone"
                    variant="outlined"
                    label="Teléfono"
                    fullWidth
                    value={formik.values.phone}
                    required
                    onChange={(event) => {
                      formik.setFieldValue("phone", event.target.value.replace(/[^0-9]/g, ""));
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.phone && Boolean(formik.errors.phone)}
                    helperText={formik.touched.phone && formik.errors.phone}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                    }}
                    disabled={formik.values.isPFAE || formik.isSubmitting}
                  />
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3} mb={2}>
                <Button type="submit" variant="default" disabled={!formik.isValid || formik.isSubmitting}>
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default DatosEmpresaPfae;
